/**
 * /isolation-pac — page service "Isolation Combles à 1€" + "Pompe à Chaleur à 1€".
 *
 * Capture les leads qualifiés (revenu fiscal, statut propriétaire/locataire,
 * type combles, etc.) pour évaluation éligibilité CEE + MaPrimeRénov'.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
    ArrowRight, ThermometerSun, Snowflake, Home, BadgeCheck, ShieldCheck,
    CheckCircle2, Sparkles, Lock,
} from "lucide-react";
import CmsBackground from "@/components/CmsBackground";
import { useSiteContent } from "@/lib/useSiteContent";
import { keysForMany } from "@/config/cmsSections";


const RFR_OPTIONS = [
    { value: "lt_20k",  label: "Moins de 20 000 €" },
    { value: "20k_40k", label: "Entre 20 000 € et 40 000 €" },
    { value: "40k_60k", label: "Entre 40 000 € et 60 000 €" },
    { value: "gt_60k",  label: "Plus de 60 000 €" },
    { value: "unknown", label: "Je préfère ne pas le dire" },
];

const STATUT_OPTIONS = [
    { value: "proprio_occupant", label: "Propriétaire occupant" },
    { value: "proprio_bailleur", label: "Propriétaire bailleur" },
    { value: "locataire",        label: "Locataire" },
];

const LOGEMENT_OPTIONS = [
    { value: "maison",      label: "Maison individuelle" },
    { value: "appartement", label: "Appartement" },
];

const COMBLES_OPTIONS = [
    { value: "perdus",       label: "Combles perdus" },
    { value: "amenageables", label: "Combles aménageables" },
    { value: "both",         label: "Les deux" },
];

const TRAVAUX_OPTIONS = [
    { value: "isolation", label: "Isolation des combles à 1€" },
    { value: "pac",       label: "Pompe à chaleur à 1€" },
    { value: "both",      label: "Isolation + PAC" },
];


export default function IsolationPAC() {
    const c = useSiteContent(keysForMany("common", "isolation-pac"));
    const [form, setForm] = useState({
        full_name: "",
        phone: "",
        email: "",
        postal_code: "",
        revenu_fiscal_bracket: "unknown",
        statut: "proprio_occupant",
        type_logement: "maison",
        surface_m2: "",
        annee_construction: "",
        type_combles: "perdus",
        travaux_souhaites: "isolation",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                surface_m2: form.surface_m2 ? Number(form.surface_m2) : null,
                annee_construction: form.annee_construction ? Number(form.annee_construction) : null,
            };
            await api.post("/isolation-pac/leads", payload);
            setSubmitted(true);
            toast.success("Demande envoyée — un expert vous rappelle sous 24h");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    return (
        <div data-testid="isolation-pac-page">
            {/* HERO */}
            <section className="relative em-grain bg-secondary overflow-hidden -mt-20 md:-mt-20 min-h-screen flex items-center">
                <CmsBackground cmsKey="iso.hero.bg" />
                <div className="relative z-10 em-container pt-40 md:pt-44 pb-20 w-full">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <p className="em-eyebrow flex items-center gap-2">
                                <BadgeCheck className="h-3.5 w-3.5" />
                                {c("iso.hero.eyebrow", "Aides État · Particuliers")}
                            </p>
                            <h1
                                className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight text-primary leading-[1.05] mt-3"
                                dangerouslySetInnerHTML={{
                                    __html: c("iso.hero.title", "Isolez vos combles<br /><span class=\"bg-accent text-primary px-3 rounded-md\">à partir de 1€</span>"),
                                }}
                            />
                            <p className="text-lg md:text-xl text-muted-foreground mt-6 max-w-xl leading-relaxed">
                                {c("iso.hero.subtitle", "Profitez des aides Certificats d'Économies d'Énergie (CEE) et MaPrimeRénov' pour isoler vos combles et installer une pompe à chaleur. Étude gratuite, sans engagement.")}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 mt-7">
                                <Button asChild size="lg" className="rounded-full bg-primary hover:bg-primary/90 px-7 h-12">
                                    <a href="#form-eligibilite">
                                        <ThermometerSun className="h-4 w-4 mr-2" />
                                        {c("iso.hero.cta_primary", "Vérifier mon éligibilité")}
                                    </a>
                                </Button>
                                <Button asChild size="lg" variant="outline" className="rounded-full px-7 h-12">
                                    <a href="#comment">
                                        {c("iso.hero.cta_secondary", "Comment ça marche")}
                                    </a>
                                </Button>
                            </div>
                            <div className="flex items-center gap-3 mt-7 text-sm text-muted-foreground">
                                <ShieldCheck className="h-4 w-4 text-primary" />
                                {c("iso.hero.trust", "RGE QualiBat · Délégataire CEE · Garantie décennale")}
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="grid grid-cols-1 gap-4">
                                <KPI
                                    icon={Home}
                                    label={c("iso.hero.kpi1.label", "Isolation combles")}
                                    value={c("iso.hero.kpi1.value", "dès 1€")}
                                    hint={c("iso.hero.kpi1.hint", "selon revenu fiscal")}
                                />
                                <KPI
                                    icon={Snowflake}
                                    label={c("iso.hero.kpi2.label", "Pompe à chaleur")}
                                    value={c("iso.hero.kpi2.value", "dès 1€")}
                                    hint={c("iso.hero.kpi2.hint", "Air/Eau ou Air/Air")}
                                />
                                <KPI
                                    icon={Sparkles}
                                    label={c("iso.hero.kpi3.label", "Économies")}
                                    value={c("iso.hero.kpi3.value", "jusqu'à 70%")}
                                    hint={c("iso.hero.kpi3.hint", "sur la facture chauffage")}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* TRAVAUX — 2 cards */}
            <section id="comment" className="em-section em-container">
                <p className="em-eyebrow">{c("iso.works.eyebrow", "Les deux travaux phares")}</p>
                <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-primary max-w-2xl mt-2">
                    {c("iso.works.title", "Isoler + chauffer mieux pour payer moins.")}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
                    <Card className="rounded-3xl border-border em-card-hover">
                        <CardContent className="p-8">
                            <div className="h-12 w-12 rounded-xl bg-accent flex items-center justify-center">
                                <Home className="h-6 w-6 text-accent-foreground" />
                            </div>
                            <h3 className="font-display text-2xl font-bold text-primary mt-5">{c("iso.work1.title", "Isolation des combles à 1€")}</h3>
                            <p className="text-muted-foreground mt-2 leading-relaxed">
                                {c("iso.work1.body", "Jusqu'à 30% des déperditions thermiques d'une maison passent par les combles. L'isolation est entièrement financée par les CEE pour les foyers modestes.")}
                            </p>
                            <ul className="mt-5 space-y-2 text-sm">
                                <li className="flex gap-2"><BadgeCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />{c("iso.work1.bullet1", "Travaux réalisés en 1 journée")}</li>
                                <li className="flex gap-2"><BadgeCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />{c("iso.work1.bullet2", "Artisans RGE QualiBat")}</li>
                                <li className="flex gap-2"><BadgeCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />{c("iso.work1.bullet3", "Garantie décennale")}</li>
                            </ul>
                        </CardContent>
                    </Card>
                    <Card className="rounded-3xl bg-primary text-primary-foreground border-0 em-card-hover">
                        <CardContent className="p-8">
                            <div className="h-12 w-12 rounded-xl bg-accent flex items-center justify-center">
                                <Snowflake className="h-6 w-6 text-accent-foreground" />
                            </div>
                            <h3 className="font-display text-2xl font-bold mt-5">{c("iso.work2.title", "Pompe à chaleur à 1€")}</h3>
                            <p className="text-primary-foreground/80 mt-2 leading-relaxed">
                                {c("iso.work2.body", "Remplacez votre vieille chaudière par une PAC Air/Eau ou Air/Air. Aides cumulables : CEE + MaPrimeRénov' + éco-PTZ. Reste à charge à partir de 1€ selon revenus.")}
                            </p>
                            <ul className="mt-5 space-y-2 text-sm">
                                <li className="flex gap-2"><BadgeCheck className="h-4 w-4 text-accent shrink-0 mt-0.5" />{c("iso.work2.bullet1", "Diagnostic thermique offert")}</li>
                                <li className="flex gap-2"><BadgeCheck className="h-4 w-4 text-accent shrink-0 mt-0.5" />{c("iso.work2.bullet2", "Économies 60-75% chauffage")}</li>
                                <li className="flex gap-2"><BadgeCheck className="h-4 w-4 text-accent shrink-0 mt-0.5" />{c("iso.work2.bullet3", "Installateur RGE QualiPAC")}</li>
                            </ul>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* FORM ÉLIGIBILITÉ */}
            <section id="form-eligibilite" className="em-section bg-secondary">
                <div className="em-container max-w-3xl">
                    <p className="em-eyebrow">{c("iso.form.eyebrow", "Test d'éligibilité")}</p>
                    <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-primary mt-2">
                        {c("iso.form.title", "Vérifiez votre éligibilité en 2 minutes.")}
                    </h2>
                    <p className="text-muted-foreground mt-3">
                        {c("iso.form.body", "Renseignez votre profil et nous calculons les aides auxquelles vous avez droit. Un expert vous rappelle sous 24h pour confirmer.")}
                    </p>

                    {submitted ? (
                        <Card className="rounded-3xl mt-8 bg-primary text-primary-foreground border-0">
                            <CardContent className="p-10 text-center">
                                <CheckCircle2 className="h-16 w-16 mx-auto text-accent" />
                                <h3 className="font-display text-3xl font-black mt-4">{c("iso.form.success_title", "Demande reçue !")}</h3>
                                <p className="text-primary-foreground/80 mt-3">
                                    {c("iso.form.success_body", "Merci. Un expert vous contacte sous 24h ouvrées pour confirmer votre éligibilité et planifier la visite technique.")}
                                </p>
                                <Button asChild className="rounded-full mt-6 bg-accent text-accent-foreground font-bold">
                                    <Link to="/">{c("common.cta.back_home", "Retour à l'accueil")}</Link>
                                </Button>
                            </CardContent>
                        </Card>
                    ) : (
                        <Card className="rounded-3xl border-border mt-8 bg-card">
                            <CardContent className="p-7">
                                <form onSubmit={submit} className="space-y-5">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <FormField label="Nom et prénom" required value={form.full_name} onChange={(v) => update("full_name", v)} />
                                        <FormField label="Téléphone" required value={form.phone} onChange={(v) => update("phone", v)} />
                                        <FormField label="Email" required type="email" value={form.email} onChange={(v) => update("email", v)} />
                                        <FormField label="Code postal" value={form.postal_code} onChange={(v) => update("postal_code", v)} />
                                    </div>

                                    <SelectRow label="Revenu fiscal de référence" required value={form.revenu_fiscal_bracket} onChange={(v) => update("revenu_fiscal_bracket", v)} options={RFR_OPTIONS} />
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <SelectRow label="Statut" required value={form.statut} onChange={(v) => update("statut", v)} options={STATUT_OPTIONS} />
                                        <SelectRow label="Type de logement" required value={form.type_logement} onChange={(v) => update("type_logement", v)} options={LOGEMENT_OPTIONS} />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <FormField label="Surface (m²)" type="number" value={form.surface_m2} onChange={(v) => update("surface_m2", v)} />
                                        <FormField label="Année de construction" type="number" value={form.annee_construction} onChange={(v) => update("annee_construction", v)} />
                                    </div>
                                    <SelectRow label="Type de combles" value={form.type_combles} onChange={(v) => update("type_combles", v)} options={COMBLES_OPTIONS} />
                                    <SelectRow label="Travaux souhaités" required value={form.travaux_souhaites} onChange={(v) => update("travaux_souhaites", v)} options={TRAVAUX_OPTIONS} />

                                    <div>
                                        <Label className="em-eyebrow">Message (optionnel)</Label>
                                        <Textarea
                                            value={form.message}
                                            onChange={(e) => update("message", e.target.value)}
                                            rows={3}
                                            placeholder="Précisez vos besoins, contraintes ou questions…"
                                            className="rounded-xl mt-2"
                                        />
                                    </div>

                                    {/* RGPD notice */}
                                    <p className="text-xs text-muted-foreground flex items-start gap-2 pt-2 border-t border-border">
                                        <Lock className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                        Vos données sont protégées et ne seront jamais transmises à des tiers sans votre accord. Conformément au RGPD, vous disposez d'un droit d'accès, de rectification et de suppression.
                                    </p>

                                    <Button type="submit" disabled={submitting} size="lg" className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold h-12">
                                        {submitting ? "Envoi…" : c("iso.form.submit", "Vérifier mon éligibilité")}
                                        <ArrowRight className="h-4 w-4 ml-2" />
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </section>
        </div>
    );
}


function KPI({ icon: Icon, label, value, hint }) {
    return (
        <Card className="rounded-2xl border-border">
            <CardContent className="p-5 flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-accent flex items-center justify-center shrink-0">
                    <Icon className="h-6 w-6 text-accent-foreground" />
                </div>
                <div>
                    <p className="em-eyebrow">{label}</p>
                    <p className="font-display text-2xl font-black text-primary">{value}</p>
                    {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
                </div>
            </CardContent>
        </Card>
    );
}


function FormField({ label, value, onChange, required, type = "text" }) {
    return (
        <div>
            <Label className="em-eyebrow">{label}{required && " *"}</Label>
            <Input
                type={type}
                required={required}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="rounded-xl mt-2"
            />
        </div>
    );
}


function SelectRow({ label, value, onChange, options, required }) {
    return (
        <div>
            <Label className="em-eyebrow">{label}{required && " *"}</Label>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger className="rounded-xl mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                    {options.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}
